import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../shared/components/Button';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { useClaimPermission } from '../../../shared/hooks/useClaimPermission';
import {
  useCreateDepartment,
  useCreateDepartmentRole,
  useDepartmentRoles,
  useDepartments,
  useToggleDepartmentActive,
  useToggleDepartmentRoleActive,
  useUpdateDepartment,
  useUpdateDepartmentRole,
} from '../hooks/useDepartments';
import { NameDescriptionModal } from '../components/NameDescriptionModal';
import { PermissionsMatrixModal } from '../components/PermissionsMatrixModal';
import { StaffAssignmentsPanel } from '../components/StaffAssignmentsPanel';
import type { Department, DepartmentRole } from '../types';

/**
 * Every department write is audited by watchman's interceptor as
 * resource=DEPARTMENT / service=watchman-service into the central trail, which
 * is the only history there is — there is no per-department timeline endpoint.
 */
const HISTORY_LINK = '/audit-logs?resource=DEPARTMENT&service=watchman-service';

const ActiveBadge = ({ isActive }: { isActive: boolean }) => (
  <span
    className={`text-xs px-2 py-0.5 rounded-full ${
      isActive ? 'bg-green-100 text-green-800' : 'bg-muted text-muted-foreground'
    }`}>
    {isActive ? 'Active' : 'Inactive'}
  </span>
);

/**
 * TCS departments, roles & permissions (PE-2147, backend PE-2146).
 *
 * Visibility is the static DEPARTMENT_VIEW/MANAGE role map (superadmin + admin,
 * mirroring watchman's seed). Write buttons are additionally enabled through
 * `useClaimPermission`, so a staff member whose *department role* grants
 * DEPARTMENT:MANAGE can manage here even if their base role could not.
 */
export const DepartmentsPage = () => {
  const { hasPermission } = usePermissions();
  const { hasClaim } = useClaimPermission();
  const canManage = hasPermission(Permission.DEPARTMENT_MANAGE) || hasClaim('DEPARTMENT', 'MANAGE');
  // CREATE/UPDATE are seeded together with MANAGE; a department role could grant them separately.
  const canEdit = canManage || hasClaim('DEPARTMENT', 'CREATE') || hasClaim('DEPARTMENT', 'UPDATE');

  const { data: departments = [], isLoading, error } = useDepartments();
  const [selectedId, setSelectedId] = useState<string | undefined>();
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    if (!selectedId && departments.length) setSelectedId(departments[0].id);
  }, [departments, selectedId]);

  const selected = departments.find((d) => d.id === selectedId);
  const { data: roles = [], isLoading: rolesLoading } = useDepartmentRoles(selected?.id);

  const createDept = useCreateDepartment();
  const updateDept = useUpdateDepartment();
  const toggleDept = useToggleDepartmentActive();
  const createRole = useCreateDepartmentRole();
  const updateRole = useUpdateDepartmentRole();
  const toggleRole = useToggleDepartmentRoleActive();

  const [deptModal, setDeptModal] = useState<'create' | Department | undefined>();
  const [roleModal, setRoleModal] = useState<'create' | DepartmentRole | undefined>();
  const [matrixRole, setMatrixRole] = useState<DepartmentRole | undefined>();

  const visibleDepartments = showInactive ? departments : departments.filter((d) => d.isActive);

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Departments</h1>
          <p className="text-sm text-muted-foreground mt-1">
            TCS departments, the roles inside them, what each role may do, and who holds it. A role's grants
            reach the assignee on their next sign-in.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Link to={HISTORY_LINK} className="text-sm text-blue-600 hover:underline">
            History
          </Link>
          {canEdit && <Button onClick={() => setDeptModal('create')}>New department</Button>}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">Could not load departments.</p>}
      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Left — departments */}
        <div className="rounded-lg border border-border bg-card p-3 space-y-2 self-start">
          <label className="flex items-center gap-2 text-xs text-muted-foreground px-1">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
          {!isLoading && !visibleDepartments.length && (
            <p className="text-sm text-muted-foreground px-1">No departments yet.</p>
          )}
          <ul className="space-y-1">
            {visibleDepartments.map((d) => (
              <li key={d.id}>
                <button
                  onClick={() => setSelectedId(d.id)}
                  className={`w-full text-left px-3 py-2 rounded-md text-sm flex items-center justify-between gap-2 ${
                    d.id === selectedId ? 'bg-muted font-semibold text-foreground' : 'hover:bg-muted/60 text-foreground'
                  }`}>
                  <span className="truncate">{d.name}</span>
                  <ActiveBadge isActive={d.isActive} />
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Right — selected department */}
        {selected ? (
          <div className="space-y-5">
            <div className="rounded-lg border border-border bg-card p-4 space-y-2">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <h2 className="text-lg font-bold text-foreground">{selected.name}</h2>
                    <ActiveBadge isActive={selected.isActive} />
                  </div>
                  {selected.description && (
                    <p className="text-sm text-muted-foreground mt-1">{selected.description}</p>
                  )}
                </div>
                {canEdit && (
                  <div className="flex items-center gap-3">
                    <button onClick={() => setDeptModal(selected)} className="text-sm text-blue-600 hover:underline">
                      Edit
                    </button>
                    <button
                      onClick={() => toggleDept.mutate({ id: selected.id, isActive: selected.isActive })}
                      disabled={toggleDept.isPending}
                      className="text-sm text-blue-600 hover:underline disabled:opacity-50">
                      {selected.isActive ? 'Deactivate' : 'Activate'}
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div className="rounded-lg border border-border bg-card p-4 space-y-3">
              <div className="flex items-center justify-between gap-4">
                <h3 className="text-base font-semibold text-foreground">Roles</h3>
                {canEdit && (
                  <Button size="sm" variant="outline" onClick={() => setRoleModal('create')}>
                    New role
                  </Button>
                )}
              </div>
              {rolesLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
              {!rolesLoading && !roles.length && (
                <p className="text-sm text-muted-foreground">No roles in this department yet.</p>
              )}
              <ul className="divide-y divide-border">
                {roles.map((role) => (
                  <li key={role.id} className="py-2 flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{role.name}</span>
                        <ActiveBadge isActive={role.isActive} />
                      </div>
                      {role.description && (
                        <p className="text-sm text-muted-foreground mt-0.5">{role.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setMatrixRole(role)} className="text-sm text-blue-600 hover:underline">
                        Permissions
                      </button>
                      {canEdit && (
                        <>
                          <button onClick={() => setRoleModal(role)} className="text-sm text-blue-600 hover:underline">
                            Edit
                          </button>
                          <button
                            onClick={() =>
                              toggleRole.mutate({ id: role.id, departmentId: selected.id, isActive: role.isActive })
                            }
                            disabled={toggleRole.isPending}
                            className="text-sm text-blue-600 hover:underline disabled:opacity-50">
                            {role.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-border bg-card p-4">
              <StaffAssignmentsPanel department={selected} roles={roles} canManage={canManage} />
            </div>
          </div>
        ) : (
          !isLoading && (
            <p className="text-sm text-muted-foreground">Select a department to see its roles and staff.</p>
          )
        )}
      </div>

      {deptModal && (
        <NameDescriptionModal
          title={deptModal === 'create' ? 'New department' : 'Edit department'}
          initial={deptModal === 'create' ? undefined : deptModal}
          isPending={createDept.isPending || updateDept.isPending}
          onSubmit={async (values) => {
            if (deptModal === 'create') {
              const created = await createDept.mutateAsync(values);
              setSelectedId(created.id);
            } else {
              await updateDept.mutateAsync({ id: deptModal.id, payload: values });
            }
          }}
          onClose={() => setDeptModal(undefined)}
        />
      )}

      {roleModal && selected && (
        <NameDescriptionModal
          title={roleModal === 'create' ? `New role in ${selected.name}` : 'Edit role'}
          initial={roleModal === 'create' ? undefined : roleModal}
          isPending={createRole.isPending || updateRole.isPending}
          onSubmit={(values) =>
            roleModal === 'create'
              ? createRole.mutateAsync({ ...values, departmentId: selected.id })
              : updateRole.mutateAsync({ id: roleModal.id, departmentId: selected.id, payload: values })
          }
          onClose={() => setRoleModal(undefined)}
        />
      )}

      {matrixRole && (
        <PermissionsMatrixModal role={matrixRole} canManage={canManage} onClose={() => setMatrixRole(undefined)} />
      )}
    </div>
  );
};
